/**
 * Provisional attacking-half pool bins for Insights GK location maps.
 * Image: pool-location-1500x1744_new.jpeg (goal at top, Y grows down).
 *
 * ponytail: bounds are a first cut aligned to LW/C/RW near 2m and outside from ~4m.
 * Ceiling = wrong bins if operators click deep or wide; upgrade path is nudge constants
 * after a few real matches, or click-calibrated rectangles.
 */

export type PoolZoneId = 'LW' | 'MC' | 'RW' | 'LO' | 'MO' | 'RO';

export const POOL_ZONE_IDS: PoolZoneId[] = ['LW', 'MC', 'RW', 'LO', 'MO', 'RO'];

export const POOL_ZONE_LABELS: Record<PoolZoneId, string> = {
    LW: 'Left wing',
    MC: 'Middle centre',
    RW: 'Right wing',
    LO: 'Left outside',
    MO: 'Middle outside',
    RO: 'Right outside',
};

/** Coarse column covering the three fine zones on each side. */
export type PoolColumnId = 'L' | 'M' | 'R';

export const POOL_COLUMN_LABELS: Record<PoolColumnId, string> = {
    L: 'Left',
    M: 'Middle',
    R: 'Right',
};

export const POOL_ATTACK_FRAME = {
    /** Just below the goal line graphic. */
    top: 0.04,
    /**
     * Split near / outside (~4m). Image markers on pool-location-1500x1744_new.jpeg
     * (asset reads 1376×1600): 2M red ≈0.08, 5M white ≈0.20, 6M yellow ≈0.24.
     * ponytail: 0.18 sits between 2M and 5M, just above the 5M line (smaller Y).
     * Ceiling = still provisional; nudge after real clicks if wings/outside feel off.
     */
    nearEnd: 0.18,
    /** End of “outside” band (~ through 8–10m). Deeper shots clamp into outside. */
    depthEnd: 0.55,
    left: 0,
    right: 1,
} as const;

const usable = (x?: number | null, y?: number | null): boolean =>
    x != null && y != null && x >= 0 && x <= 1 && y >= 0 && y <= 1;

export const poolColumnForX = (x: number): PoolColumnId => {
    if (x < 1 / 3) return 'L';
    if (x < 2 / 3) return 'M';
    return 'R';
};

/**
 * Bin a pool click into one of six attacking zones.
 * Returns null when coords missing; 'deep' when behind the attack band (still rare).
 */
export const poolZoneIdForLocation = (
    x?: number | null,
    y?: number | null
): PoolZoneId | 'deep' | null => {
    if (!usable(x, y)) return null;
    const px = x as number;
    const py = y as number;
    const { top, nearEnd, depthEnd } = POOL_ATTACK_FRAME;
    if (py < top) return 'deep';
    if (py > depthEnd) return 'deep';

    const col = poolColumnForX(px);
    const near = py < nearEnd;
    if (col === 'L') return near ? 'LW' : 'LO';
    if (col === 'M') return near ? 'MC' : 'MO';
    return near ? 'RW' : 'RO';
};

export const poolZoneRectPercent = (
    zone: PoolZoneId
): { left: number; top: number; width: number; height: number } => {
    const { top, nearEnd, depthEnd, left, right } = POOL_ATTACK_FRAME;
    const width = ((right - left) / 3) * 100;
    const nearH = (nearEnd - top) * 100;
    const outH = (depthEnd - nearEnd) * 100;
    // LW/MC/RW = near row; LO/MO/RO = outside row. Columns = Left | Middle | Right.
    const nearRow = zone === 'LW' || zone === 'MC' || zone === 'RW';
    const colIndex = zone === 'LW' || zone === 'LO' ? 0 : zone === 'MC' || zone === 'MO' ? 1 : 2;
    return {
        left: colIndex * width,
        top: (nearRow ? top : nearEnd) * 100,
        width,
        height: nearRow ? nearH : outH,
    };
};
